import React, { useState, useEffect } from 'react';
import { getBackendBaseUrl } from '../../../config/backendUrl';
import "./Testimonial.css"

const TESTIMONIALS_API_URL = `${getBackendBaseUrl()}/testimonials_list.php`;

// Best-effort gender guess from the first name — the testimonial form
// doesn't collect gender, so this is a heuristic (common Tamil/Indian
// name endings), not a lookup. Falls back to 'male' when unclear, since
// consonant endings dominate that bucket in practice.
function guessGender(fullName) {
  const firstName = (fullName || '').trim().split(/\s+/)[0]?.toLowerCase().replace(/[^a-z]/g, '') || '';
  if (!firstName) return null;

  const femaleEndings = ['priya', 'shree', 'sree', 'devi', 'dharani', 'isha', 'ini', 'iga', 'itha', 'ika'];
  if (femaleEndings.some(suffix => firstName.endsWith(suffix))) return 'female';

  const maleEndings = ['kumar', 'raj', 'esh', 'ith', 'nth', 'eep', 'endra', 'nath', 'an', 'ik', 'uj', 'rya', 'tya'];
  if (maleEndings.some(suffix => firstName.endsWith(suffix))) return 'male';

  return /[aeiou]$/.test(firstName) ? 'female' : 'male';
}

const AVATAR_TOP_STYLES = {
  male: ['shortFlat', 'shortRound', 'shortWaved', 'shortCurly', 'sides', 'shaggy', 'shaggyMullet'],
  female: ['bigHair', 'bob', 'bun', 'curly', 'curvy', 'dreads', 'frida', 'fro', 'froBand', 'longButNotTooLong', 'miaWallace', 'straight01', 'straight02', 'straightAndStrand'],
};

// Only the schema's lightest skin tones — every avatar, regardless of
// guessed gender, uses one of these two.
const AVATAR_SKIN_COLORS = ['edb98a', 'ffdbb4'];

// Small deterministic string hash so the same name always maps to the
// same style, without relying on DiceBear's own randomization.
function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function getAvatarUrl(name) {
  const gender = guessGender(name) || 'male';
  const styles = AVATAR_TOP_STYLES[gender];
  // Pick ONE concrete style ourselves rather than handing DiceBear a list
  // to choose from — the public API has been observed to intermittently
  // 400 on multi-value "top" query params for no discernible reason.
  const top = styles[hashString(name) % styles.length];
  const skinColor = AVATAR_SKIN_COLORS[hashString(name + 'skin') % AVATAR_SKIN_COLORS.length];

  const params = new URLSearchParams({
    seed: name,
    top,
    skinColor,
    facialHairProbability: gender === 'male' ? '40' : '0',
    // Pin to happy-looking features only — otherwise DiceBear randomizes
    // across its full set, which includes sad/angry/confused looks.
    eyes: 'happy',
    eyebrows: 'defaultNatural',
    mouth: 'smile',
  });

  return `https://api.dicebear.com/9.x/avataaars/svg?${params.toString()}`;
}

function Testimonial() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoplay] = useState(true);

  const cardsToShow = Math.min(3, testimonials.length) || 1;

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await fetch(TESTIMONIALS_API_URL);
        const data = await response.json();
        if (data.success && Array.isArray(data.testimonials)) {
          setTestimonials(data.testimonials);
        }
      } catch (error) {
        console.error('Failed to load testimonials:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  useEffect(() => {
    let interval;
    if (isAutoplay && testimonials.length > cardsToShow) {
      interval = setInterval(() => {
        handleNext();
      }, 8000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentIndex, isAutoplay, testimonials, cardsToShow]);

  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
    };
    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  const handlePrev = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? Math.max(testimonials.length - cardsToShow, 0) : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prev) =>
      prev >= testimonials.length - cardsToShow ? 0 : prev + 1
    );
  };

  const getVisibleCards = () => {
    const cards = [];
    for (let i = 0; i < cardsToShow; i++) {
      const index = (currentIndex + i) % testimonials.length;
      cards.push(testimonials[index]);
    }
    return cards;
  };

  if (loading || testimonials.length === 0) {
    return null;
  }

  return (
    <div className="testimonial-section">
      <div className="testimonial-container">
        <div className="testimonial-section-header">
          <h2 className="testimonial-section-title">Student Success Stories</h2><br/>
          <p className="testimonial-section-subtitle">Hear from our amazing students about their transformative learning experience</p>
        </div>

        <div className="testimonial-slider">
          <div className="cards-container">
            {getVisibleCards().map((testimonial, index) => (
              <div key={`${testimonial.name}-${currentIndex}-${index}`} className="testimonial-card">
                <div className="card-header">
                  <div className="student-avatar">
                    <img
                      src={getAvatarUrl(testimonial.name)}
                      alt={testimonial.name}
                      loading="lazy"
                      onError={(e) => {
                        // DiceBear's public API occasionally errors out —
                        // fall back to a simple initials avatar so the card
                        // never shows a broken image icon.
                        e.target.onerror = null;
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(testimonial.name)}&background=4679ae&color=fff&size=128`;
                      }}
                    />
                  </div>
                </div>

                <div className="card-body">
                  <div className="student-info">
                    <h3 className="student-name">{testimonial.name}</h3>
                    {(testimonial.college || testimonial.department) && (
                      <p className="student-location">
                        {[testimonial.department, testimonial.college].filter(Boolean).join(', ')}
                      </p>
                    )}
                  </div>

                  <div className="testimonial-conversation">
                    {testimonial.qa.map((pair, qaIndex) => (
                      <div className="qa-pair" key={qaIndex}>
                        <div className="chat-bubble question-bubble">{pair.question}</div>
                        <div className="chat-bubble answer-bubble">{pair.answer}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {testimonials.length > cardsToShow && (
            <div className="slider-controls">
              <button className="control-btn prev-btn" onClick={handlePrev}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              <div className="dots-indicator">
                {Array.from({ length: Math.max(testimonials.length - cardsToShow + 1, 1) }).map((_, index) => (
                  <button
                    key={index}
                    className={`dot ${index === currentIndex ? 'active' : ''}`}
                    onClick={() => setCurrentIndex(index)}
                  />
                ))}
              </div>

              <button className="control-btn next-btn" onClick={handleNext}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Testimonial;
